/**
 * telnyxService — thin module providing call control and audio device management.
 *
 * The TelnyxRTC client lifecycle (init, connect, reconnect, events) is managed
 * by TelnyxProvider.  This module holds module-level references to the client
 * and current call so that Redux thunk actions can access them without needing
 * React context.
 *
 * TelnyxProvider calls setClient / setCurrentCall / setAudioElement to keep
 * the references in sync with the React lifecycle.
 */
import _ from 'lodash';

import { logger } from 'utils/logger';

// ---------------------------------------------------------------------------
// Module-level state (set by TelnyxProvider, read by Redux actions & components)
// Uses a mutable object so we can reassign fields without `let`.
// ---------------------------------------------------------------------------

const refs = {
  client: null,
  currentCall: null,
  selectedInputDeviceId: null,
  selectedOutputDeviceId: null,
  audioElement: null,
};

// ---------------------------------------------------------------------------
// Setters — called by TelnyxProvider to keep refs in sync
// ---------------------------------------------------------------------------

export const setClient = client => {
  refs.client = client;

  // Re-apply the stored *output* device to new clients (init, reconnect, token
  // refresh).  We intentionally do NOT call client.setAudioSettings here because
  // that triggers getUserMedia, which would activate the mic on page load.
  // The mic is only routed at call time via makeCall / setAudioInDevice.
  if (client && refs.selectedOutputDeviceId) {
    try {
      client.speaker = refs.selectedOutputDeviceId;
    } catch (err) {
      logger.warn('[Telnyx] Failed to apply stored output device to new client', err);
    }
  }
};

export const setCurrentCall = call => {
  refs.currentCall = call;
};

export const setAudioElement = el => {
  refs.audioElement = el;
};

// ---------------------------------------------------------------------------
// Getters
// ---------------------------------------------------------------------------

export const getSelectedInputDeviceId = () => refs.selectedInputDeviceId;
export const getSelectedOutputDeviceId = () => refs.selectedOutputDeviceId;

// ---------------------------------------------------------------------------
// Audio device routing
// ---------------------------------------------------------------------------

export const setInputDevice = deviceId => {
  refs.selectedInputDeviceId = deviceId;
  // The mic is routed at call time via makeCall / setAudioInDevice — we do NOT
  // call client.setAudioSettings here because it triggers getUserMedia.
};

export const setOutputDevice = deviceId => {
  refs.selectedOutputDeviceId = deviceId;

  // Set speaker on the Telnyx client so all future calls use this speaker.
  if (refs.client && deviceId) {
    refs.client.speaker = deviceId;
  }

  if (refs.audioElement && _.isFunction(refs.audioElement.setSinkId) && deviceId) {
    refs.audioElement.setSinkId(deviceId).catch(err => {
      logger.warn('[Telnyx] Failed to set output device', err);
    });
  }
};

// Delay before forcing mic switch on a new call — allows peer connection to establish.
const CALL_DEVICE_SWITCH_DELAY_MS = 1000;

// ---------------------------------------------------------------------------
// Call control
// ---------------------------------------------------------------------------

export const makeCall = ({ destinationNumber, callerNumber, callerName, clientState }) => {
  if (!refs.client) {
    throw new Error('Telnyx client not initialized');
  }

  const callOptions = {
    destinationNumber,
    callerName,
    callerNumber,
    clientState,
  };

  // Pass the user-selected mic/speaker so the Telnyx SDK uses them
  // instead of the browser's default devices.
  if (refs.selectedInputDeviceId) {
    callOptions.micId = refs.selectedInputDeviceId;
  }

  if (refs.selectedOutputDeviceId) {
    callOptions.speakerId = refs.selectedOutputDeviceId;
  }

  const call = refs.client.newCall(callOptions);

  refs.currentCall = call;

  // Belt-and-suspenders: once the call's peer connection is set up,
  // force-switch to the selected mic via setAudioInDevice.
  if (refs.selectedInputDeviceId && _.isFunction(call.setAudioInDevice)) {
    // Small delay to let the call establish its peer connection first
    setTimeout(() => {
      if (refs.currentCall === call && _.isFunction(call.setAudioInDevice)) {
        call.setAudioInDevice(refs.selectedInputDeviceId).catch(err => {
          logger.warn('[Telnyx] Failed to set input device on active call', err);
        });
      }
    }, CALL_DEVICE_SWITCH_DELAY_MS);
  }

  return call;
};

export const hangup = () => {
  if (refs.currentCall) {
    refs.currentCall.hangup();
  }
};

export const answer = () => {
  if (refs.currentCall) {
    refs.currentCall.answer();
  }
};

export const hold = () => {
  if (refs.currentCall) {
    refs.currentCall.hold();
  }
};

export const unhold = () => {
  if (refs.currentCall) {
    refs.currentCall.unhold();
  }
};

export const sendDtmf = digit => {
  if (refs.currentCall) {
    refs.currentCall.dtmf(digit);
  }
};

// ---------------------------------------------------------------------------
// Default export — object with getters for backward compatibility
// ---------------------------------------------------------------------------

const telnyxService = {
  get client() {
    return refs.client;
  },

  get currentCall() {
    return refs.currentCall;
  },

  setInputDevice,
  setOutputDevice,
  makeCall,
  hangup,
  answer,
  hold,
  unhold,
  sendDtmf,
};

export default telnyxService;
